import { enhancedApi } from '@store/generated/billing';

/**
 * Feature-specific tag types for service facility cache management
 */
const SERVICE_FACILITY_TAG_TYPES = ['ServiceFacility', 'ServiceFacilityList'] as const;

/**
 * Service Facilities API - extends the generated OpenAPI client
 */
export const serviceFacilitiesApi = enhancedApi.enhanceEndpoints({
  addTagTypes: SERVICE_FACILITY_TAG_TYPES,
  endpoints: {
    getV2ProviderServiceFacilities: {
      providesTags: result =>
        result?.service_facilities
          ? [
              ...result.service_facilities.map(({ service_facility_id }) => ({
                type: 'ServiceFacility' as const,
                id: service_facility_id || 'unknown',
              })),
              { type: 'ServiceFacilityList' as const, id: 'PROVIDER' },
            ]
          : [{ type: 'ServiceFacilityList', id: 'PROVIDER' }],
    },

    getV2ProviderServiceFacilitiesByServiceFacilityId: {
      providesTags: (_result, _error, arg) => [
        { type: 'ServiceFacility', id: arg.serviceFacilityId },
      ],
    },

    postV2ProviderServiceFacilities: {
      invalidatesTags: [{ type: 'ServiceFacilityList', id: 'PROVIDER' }],
    },

    putV2ProviderServiceFacilitiesByServiceFacilityId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ServiceFacility', id: arg.serviceFacilityId },
        { type: 'ServiceFacilityList', id: 'PROVIDER' },
      ],
    },

    deleteV2ProviderServiceFacilitiesByServiceFacilityId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ServiceFacility', id: arg.serviceFacilityId },
        { type: 'ServiceFacilityList', id: 'PROVIDER' },
      ],
    },

    postV2ProviderServiceFacilitiesByServiceFacilityIdSetDefault: {
      invalidatesTags: [{ type: 'ServiceFacilityList', id: 'PROVIDER' }],
    },

    // Staff (admin v2) endpoints, scoped to the viewed organization
    getV2AdminOrgsByOrganizationIdServiceFacilities: {
      providesTags: (result, _error, arg) =>
        result?.service_facilities
          ? [
              ...result.service_facilities.map(({ service_facility_id }) => ({
                type: 'ServiceFacility' as const,
                id: service_facility_id || 'unknown',
              })),
              { type: 'ServiceFacilityList' as const, id: arg.organizationId },
            ]
          : [{ type: 'ServiceFacilityList', id: arg.organizationId }],
    },

    getV2AdminOrgsByOrganizationIdServiceFacilitiesAndServiceFacilityId: {
      providesTags: (_result, _error, arg) => [
        { type: 'ServiceFacility', id: arg.serviceFacilityId },
      ],
    },

    postV2AdminOrgsByOrganizationIdServiceFacilities: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ServiceFacilityList', id: arg.organizationId },
      ],
    },

    putV2AdminOrgsByOrganizationIdServiceFacilitiesAndServiceFacilityId: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ServiceFacility', id: arg.serviceFacilityId },
        { type: 'ServiceFacilityList', id: arg.organizationId },
      ],
    },
  },
});

// Export hooks with user-friendly names
export const {
  // Queries
  useGetV2ProviderServiceFacilitiesQuery: useGetServiceFacilitiesQuery,
  useLazyGetV2ProviderServiceFacilitiesQuery: useLazyGetServiceFacilitiesQuery,
  useGetV2ProviderServiceFacilitiesByServiceFacilityIdQuery: useGetServiceFacilityQuery,
  useLazyGetV2ProviderServiceFacilitiesByServiceFacilityIdQuery: useLazyGetServiceFacilityQuery,

  // Mutations
  usePostV2ProviderServiceFacilitiesMutation: useCreateServiceFacilityMutation,
  usePutV2ProviderServiceFacilitiesByServiceFacilityIdMutation: useUpdateServiceFacilityMutation,
  useDeleteV2ProviderServiceFacilitiesByServiceFacilityIdMutation: useDeleteServiceFacilityMutation,
  usePostV2ProviderServiceFacilitiesByServiceFacilityIdSetDefaultMutation:
    useSetDefaultServiceFacilityMutation,

  // Staff (admin v2) queries
  useGetV2AdminOrgsByOrganizationIdServiceFacilitiesQuery: useGetServiceFacilitiesAdminQuery,
  useGetV2AdminOrgsByOrganizationIdServiceFacilitiesAndServiceFacilityIdQuery:
    useGetServiceFacilityAdminQuery,

  // Staff (admin v2) mutations
  usePostV2AdminOrgsByOrganizationIdServiceFacilitiesMutation:
    useCreateServiceFacilityAdminMutation,
  usePutV2AdminOrgsByOrganizationIdServiceFacilitiesAndServiceFacilityIdMutation:
    useUpdateServiceFacilityAdminMutation,
} = serviceFacilitiesApi;
